/**
 * Raw row helpers shared across every Plant Viewer cypher transform.
 * Moved here from `domains/ontology-ui/state/src/api/ontology-api.ts`
 * so they're framework-free (no RTK Query, no Neo4j driver) and can
 * be reused by every endpoint transform without dragging the API
 * slice along.
 */

import { extractPlantMeta } from './extract/provenance'

/**
 * Narrow an unknown cypher cell into a raw node record (anything with
 * a string `id` or `_id`). Returns `null` for sentinel rows that the
 * cypher's `OPTIONAL MATCH` left unbound.
 */
export function extractRawNode(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object') return null
  const node = value as Record<string, unknown>
  if (typeof node['_id'] !== 'string' && typeof node['id'] !== 'string') return null
  return node
}

/**
 * Project a Cypher-returned node onto a Plant* type. We require an `id` (or
 * fall back to `_id`) and `name` since every Plant* shape declares them.
 */
export function extractNode<T extends { id: string; _id: string; name: string }>(
  value: unknown
): T | null {
  const raw = extractRawNode(value)
  if (!raw) return null
  const id = (typeof raw['id'] === 'string' ? raw['id'] : raw['_id']) as string
  const elementId = (typeof raw['_id'] === 'string' ? raw['_id'] : id) as string
  const name = (typeof raw['name'] === 'string' ? raw['name'] : id) as string
  return { ...raw, id, _id: elementId, name, ...extractPlantMeta(raw) } as unknown as T
}

export function extractNodeList<T extends { id: string; _id: string; name: string }>(
  value: unknown
): T[] {
  if (!Array.isArray(value)) return []
  return value.map((item) => extractNode<T>(item)).filter((node): node is T => node !== null)
}

/**
 * Coerce a Cypher `count(...)` value into a non-negative integer. Neo4j
 * returns counts as plain JS numbers in our wire format (the proxy
 * normalizes `Integer` → number), but we defensively narrow here in
 * case a row carries a string or `null`.
 */
export function toIntCount(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.max(0, Math.trunc(value))
  if (typeof value === 'string') {
    const n = Number.parseInt(value, 10)
    return Number.isFinite(n) ? Math.max(0, n) : 0
  }
  return 0
}

/**
 * Last `/`-delimited segment of an Ignition tag path
 * (e.g. `Plant/PST-201/Flow` → `Flow`). Returns `undefined` for
 * `undefined` input; paths without a slash are returned unchanged.
 */
export function leafTagSegment(name: string | undefined): string | undefined {
  if (!name) return undefined
  const idx = name.lastIndexOf('/')
  return idx >= 0 ? name.slice(idx + 1) : name
}
